/**
 * Capture page routes (src/aithora/v3/finance/capture.ts, capture-work.ts and
 * performance.ts). Every route here is served.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  CaptureActionBody,
  CaptureActionResult,
  CaptureEvents,
  CaptureEvidence,
  CaptureForecast,
  CaptureNudgeResult,
  CaptureQualification,
  CapturePractitioners,
  CaptureQueue,
  CaptureReadiness,
  CaptureSummaryV3,
  CaptureTaskResult,
  ClaimSignResult,
  ClearinghouseBatches,
  CodeAccessRequestResult,
  CaptureAppealDraft,
  CaptureContractRates,
  CaptureDenials,
  CaptureEasiestWins,
  CapturePaidTrend,
  CaptureShareAgreementBody,
  CaptureShareAgreements,
  CodeAccessRequests,
  FeeScheduleResponse,
  SendForSignatureResult,
} from './capture-types';

const A = AITHORA_PREFIX;
/** facilities: the facility switcher's pick (id,id), Aithora readers only; capture/summary and capture/queue take it. */
type Scope = { facility?: string; facilities?: string; period?: string };

/** GET /capture/summary: the three figures (never summed), the split and the code lines. */
export const captureSummaryV3 = route<CaptureSummaryV3, Scope>()('GET', `${A}/capture/summary`, { honest: true });
/** GET /capture/queue: stuck claims grouped by why they are stuck. */
export const captureQueue = route<CaptureQueue, Scope>()('GET', `${A}/capture/queue`, { honest: true });
/**
 * GET /capture/forecast: month-end thresholds per time- or device-based code, and for one
 * building the 3, 6 and 12 month ranges with sliders (scenario preset or each assumption).
 */
export const captureForecast = route<CaptureForecast, Scope & {
  scenario?: 'cautious' | 'expected' | 'strong'; consentRate?: number; uptake?: number; denialRate?: number; census?: number; maShare?: number;
}>()('GET', `${A}/capture/forecast`, { honest: true });
/**
 * GET /capture/qualification: live qualification this month, per resident per time- or
 * device-based code (one building; pass residentId for one resident). A month in progress
 * is never submitted; this says who has qualified and what each claim still needs.
 */
export const captureQualification = route<CaptureQualification, { facility?: string; residentId?: string }>()('GET', `${A}/capture/qualification`, { honest: true });
/** GET /capture/claims/:id/evidence: the proof bundle for one claim. */
export const captureEvidence = route<CaptureEvidence>()('GET', `${A}/capture/claims/:id/evidence`, { honest: true });
/** POST /capture/actions: a per-row or per-group action, recorded to the audit table. */
export const captureAction = route<CaptureActionResult, void, CaptureActionBody>()('POST', `${A}/capture/actions`);
/**
 * POST /capture/nudge: tell the owner a month-end threshold is at risk. A masked row sends
 * its nudgeKey instead of the code.
 */
export const captureNudge = route<CaptureNudgeResult, void, { residentId: string; code?: string; nudgeKey?: string; period?: string }>()('POST', `${A}/capture/nudge`);
/** POST /capture/claims/send-for-signature: route READY claims to the practitioner. Never submits. */
export const captureSendForSignature = route<SendForSignatureResult, void, { claimIds: string[] }>()('POST', `${A}/capture/claims/send-for-signature`);
/** GET /capture/claims/awaiting-my-signature: claims sent to the signed-in practitioner (their NPI) to sign. */
export const captureClaimsAwaitingMine = route<{
  real: true; npi: string | null;
  claims: { id: string; residentId: string; resident: string | null; cptCode: string; period: string; amountCents: number }[];
}>()('GET', `${A}/capture/claims/awaiting-my-signature`, { honest: true });
/** POST /capture/claims/:id/sign: the billing practitioner signs from their own login. */
export const captureSignClaim = route<ClaimSignResult, void, { attest: true }>()('POST', `${A}/capture/claims/:id/sign`);
/** GET /capture/practitioners: enrollment (PECOS, Medicaid) and revalidation. */
export const capturePractitioners = route<CapturePractitioners, { facility?: string }>()('GET', `${A}/capture/practitioners`, { honest: true });
/** POST /capture/practitioners/:id/billing-agent-authorizations */
export const captureAuthorizeBillingAgent = route<{ ok: true; id: string; real: true }, void, { authorizedAt?: string; documentRef: string }>()(
  'POST', `${A}/capture/practitioners/:id/billing-agent-authorizations`);
/** POST /capture/billing-agent-authorizations/:id/revoke */
export const captureRevokeBillingAgent = route<{ ok: true; id: string; real: true }, void, { reason?: string }>()('POST', `${A}/capture/billing-agent-authorizations/:id/revoke`);
/** GET /capture/clearinghouse/batches: batches with their 999 and 277CA acknowledgements. */
export const clearinghouseBatches = route<ClearinghouseBatches, { facility?: string }>()('GET', `${A}/capture/clearinghouse/batches`, { honest: true });
/** POST /capture/code-access-request: a Facility Admin asks for code-level access. */
export const codeAccessRequest = route<CodeAccessRequestResult, void, { reason?: string }>()('POST', `${A}/capture/code-access-request`);
/** GET /capture/code-access-requests */
export const codeAccessRequests = route<CodeAccessRequests, { status?: string }>()('GET', `${A}/capture/code-access-requests`, { honest: true });
/** GET /billing/fee-schedule: the conversion factor, its year and every code's rate. */
export const feeSchedule = route<FeeScheduleResponse>()('GET', `${A}/billing/fee-schedule`, { honest: true });

// ── The live feed, readiness and follow-ups (capture-work.ts) ───────────────

/** GET /capture/events: the live capture feed (the LIVE line under the figures). */
export const captureEvents = route<CaptureEvents, { facility?: string; limit?: number }>()('GET', `${A}/capture/events`, { honest: true });
/** GET /capture/readiness: every resident, every gate a claim needs. */
export const captureReadiness = route<CaptureReadiness, { facility?: string }>()('GET', `${A}/capture/readiness`, { honest: true });
/** POST /capture/practitioners/:id/actions: chase enrollment, start revalidation or enrollment. */
export const capturePractitionerAction = route<CaptureTaskResult, void, { action: 'chase_enrollment' | 'start_revalidation' | 'start_enrollment' }>()(
  'POST', `${A}/capture/practitioners/:id/actions`);
/** POST /capture/clearinghouse/batches/:batchId/resend: fix the rejected claims and resend. */
export const captureBatchResend = route<CaptureTaskResult, void, { confirm?: true }>()('POST', `${A}/capture/clearinghouse/batches/:batchId/resend`);
/** POST /capture/codes/:code/enroll: queue enrollment (Aithora captures) or a flag (the facility performs). */
export const captureCodeEnroll = route<CaptureTaskResult, void, { facility?: string; kind: 'enroll' | 'flag' }>()('POST', `${A}/capture/codes/:code/enroll`);
/** POST /capture/coverage/:id/actions: act on a payer change (re-verify, remind family, update profile). */
export const captureCoverageAction = route<CaptureTaskResult, void, { action: string }>()('POST', `${A}/capture/coverage/:id/actions`);
/** POST /capture/prevention-rules: store and apply a fix-at-source rule for a denial cause. */
export const capturePreventionRule = route<CaptureTaskResult, void, { causeId: string }>()('POST', `${A}/capture/prevention-rules`);

// ── Money paid by code, payer and practitioner (capture-paid.ts) ─────────────

/**
 * GET /capture/paid-trend: 12 months of paid and denied cents by month, code, payer class and
 * practitioner, with the building's share from the agreement on file (measured, claims_history).
 */
export const capturePaidTrend = route<CapturePaidTrend, Scope & { through?: string }>()('GET', `${A}/capture/paid-trend`, { honest: true });
/** GET /capture/share-agreements: the building's share agreements with its practitioners. */
export const captureShareAgreements = route<CaptureShareAgreements, { facility?: string }>()('GET', `${A}/capture/share-agreements`, { honest: true });
/** POST /capture/share-agreements: record a building-share agreement (Facility Admin or Aithora staff). */
export const captureSetShareAgreement = route<{ ok: true; id: string; real: true }, void, CaptureShareAgreementBody>()('POST', `${A}/capture/share-agreements`);
/**
 * GET /capture/easiest-wins: the building's unbilled codes this month ranked by what is already
 * in place, with plain blockers, modeled dollars, conflict-blocked codes apart, and care delivered vs billed.
 */
export const captureEasiestWins = route<CaptureEasiestWins, { facility?: string }>()('GET', `${A}/capture/easiest-wins`, { honest: true });
/**
 * GET /capture/denials: every denied line in one building with its reasons in plain English,
 * the fix, the deadline and a modeled chance of recovery, best chance first.
 */
export const captureDenials = route<CaptureDenials, { facility?: string }>()('GET', `${A}/capture/denials`, { honest: true });
/** GET /capture/denials/:id/appeal-draft: a draft appeal for a person to review and send; never sent. */
export const captureAppealDraft = route<CaptureAppealDraft, { facility?: string }>()('GET', `${A}/capture/denials/:id/appeal-draft`, { honest: true });
/**
 * GET /capture/contract-rates: per code, the Medicare rate for the building's locality
 * (facility and non-facility) and the published commercial market range, or why there is none.
 */
export const captureContractRates = route<CaptureContractRates, { facility?: string; payer?: string; codes?: string }>()('GET', `${A}/capture/contract-rates`, { honest: true });

// ── Audit pack and audit-risk score (AF-1654, src/aithora/v3/finance/capture-audit.ts) ──
export interface AuditLedgerRef { entryId: string | null; hash: string | null; verified: boolean | null; note: string | null }
export interface CaptureAuditPack {
  claimId: string;
  facilityId: string;
  residentId: string;
  resident: string;
  /** Null when this caller's codes are masked. */
  code: string | null;
  period: string;
  items: Array<{ key: 'consent' | 'minutes' | 'care_plan' | 'signature' | 'signing_check' | 'claim'; label: string; present: boolean; data: unknown; ledger: AuditLedgerRef | AuditLedgerRef[] }>;
  /** Atlas's minutes: documentation support, never counted toward the threshold. */
  documentationSupport: { label: string; minutes: number; counted: false; entries: Array<{ at: string | null; minutes: number; source: string }> };
  /** Everything not on file, listed and never filled in. */
  missing: Array<{ item: string; what: string }>;
  assembledAt: string;
  masked: boolean;
  real: true;
}
export interface CaptureAuditRiskMeasure {
  key: string; label: string; value: number | null; national: number | null; ratio: number | null; points: number;
  comparison: 'compared' | 'no_national_norm' | 'too_few_claims'; why: string | null;
}
export interface CaptureAuditRisk {
  facilityId: string;
  period: string;
  window: { from: string; to: string };
  /** The scoring rule, MODELED. Never a probability of audit. */
  rule: string;
  source: { name: string; url: string; version: string; retrieved: string };
  practitioners: Array<{
    practitionerId: string; npi: string | null; practitioner: string | null; code: string | null; months: number; residents: number;
    measures: CaptureAuditRiskMeasure[]; score: number; level: 'low' | 'watch' | 'elevated'; basis: 'modeled';
  }>;
  masked: boolean;
  real: true;
}
/** GET /capture/claims/:id/audit-pack: what Medicare would ask for on one claim, only what exists (?format=txt for the printable summary). */
export const captureAuditPack = route<CaptureAuditPack, { format?: 'json' | 'txt' }>()('GET', `${A}/capture/claims/:id/audit-pack`, { honest: true });
/** GET /capture/audit-risk: per practitioner and code, the billing pattern against national norms. MODELED. */
export const captureAuditRisk = route<CaptureAuditRisk, { facility?: string; period?: string }>()('GET', `${A}/capture/audit-risk`, { honest: true });

export const captureApi = {
  captureSummaryV3,
  captureQueue,
  captureForecast,
  captureQualification,
  captureEvidence,
  captureAction,
  captureNudge,
  captureSendForSignature,
  captureSignClaim,
  capturePractitioners,
  captureAuthorizeBillingAgent,
  captureRevokeBillingAgent,
  clearinghouseBatches,
  codeAccessRequest,
  codeAccessRequests,
  feeSchedule,
  captureEvents,
  captureReadiness,
  capturePractitionerAction,
  captureBatchResend,
  captureCodeEnroll,
  captureCoverageAction,
  capturePreventionRule,
  capturePaidTrend,
  captureShareAgreements,
  captureSetShareAgreement,
  captureContractRates,
  captureDenials,
  captureAppealDraft,
  captureEasiestWins,
  captureAuditPack,
  captureAuditRisk,
} as const;
